import { useState, useEffect } from 'react';
import { 
  HeadphonesIcon, 
  MessageSquare,
  Clock,
  CheckCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  User
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { supportAPI } from '../lib/api';
import { formatDateTime, getStatusColor, getPriorityColor } from '../lib/utils';
import { toast } from 'sonner';

const StatusBadge = ({ status }) => {
  const color = getStatusColor(status);
  const colorClasses = {
    success: 'badge-success',
    warning: 'badge-warning',
    danger: 'badge-danger',
    info: 'badge-info',
    neutral: 'badge-neutral',
  };
  
  return (
    <span className={`badge ${colorClasses[color]}`}>
      {status?.replace('_', ' ')}
    </span>
  );
};

export default function Support() {
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [limit] = useState(20);
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [updateDialog, setUpdateDialog] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [resolution, setResolution] = useState('');
  const [newStatus, setNewStatus] = useState('');

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const params = {
        skip: page * limit,
        limit,
        status: statusFilter || undefined,
        priority: priorityFilter || undefined,
      };
      const response = await supportAPI.getTickets(params);
      setTickets(response.data.tickets);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [page, statusFilter, priorityFilter]);

  const handleUpdate = async () => {
    try {
      const data = {
        status: newStatus,
        resolution: resolution || undefined,
      };
      await supportAPI.updateTicket(selectedTicket.id, data);
      toast.success('Ticket updated');
      setUpdateDialog(false);
      setSelectedTicket(null);
      setResolution('');
      setNewStatus('');
      fetchTickets();
    } catch (error) {
      toast.error('Failed to update ticket');
    }
  };

  const openUpdateDialog = (ticket) => {
    setSelectedTicket(ticket);
    setNewStatus(ticket.status);
    setResolution(ticket.resolution || '');
    setUpdateDialog(true);
  };

  const openCount = tickets.filter(t => t.status === 'open').length;
  const inProgressCount = tickets.filter(t => t.status === 'in_progress').length;

  return (
    <div data-testid="support-page">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Customer Support</h1>
        <p className="page-subtitle">Manage support tickets and user queries</p>
      </div>

      {/* Stats */}
      <div className="widget-grid mb-6">
        <div className="stat-card">
          <AlertCircle className="w-5 h-5 text-amber-400 mb-2" />
          <p className="stat-value text-amber-400">{openCount}</p>
          <p className="stat-label">Open Tickets</p>
        </div>
        <div className="stat-card">
          <Clock className="w-5 h-5 text-blue-400 mb-2" />
          <p className="stat-value">{inProgressCount}</p>
          <p className="stat-label">In Progress</p>
        </div>
        <div className="stat-card">
          <CheckCircle className="w-5 h-5 text-emerald-400 mb-2" />
          <p className="stat-value">{tickets.filter(t => t.status === 'resolved').length}</p>
          <p className="stat-label">Resolved Today</p>
        </div>
        <div className="stat-card">
          <MessageSquare className="w-5 h-5 text-violet-400 mb-2" />
          <p className="stat-value">{total}</p>
          <p className="stat-label">Total Tickets</p>
        </div>
      </div>

      {/* Table */}
      <div className="data-table-container">
        <div className="data-table-header">
          <h3 className="data-table-title">Support Tickets</h3>
          <div className="filter-bar">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36" data-testid="ticket-status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-36" data-testid="ticket-priority-filter">
                <SelectValue placeholder="All Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priority</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow className="table-header">
              <TableHead>Ticket</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                </TableCell>
              </TableRow>
            ) : tickets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                  No tickets found
                </TableCell>
              </TableRow>
            ) : (
              tickets.map((ticket) => (
                <TableRow key={ticket.id} className="table-row" data-testid={`ticket-row-${ticket.id}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{ticket.subject}</p>
                      <p className="text-xs text-muted-foreground line-clamp-1">{ticket.description}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{ticket.user_name}</p>
                        <p className="text-xs text-muted-foreground font-mono">{ticket.user_phone || ticket.user_id || '—'}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="badge badge-neutral capitalize">{ticket.category}</span>
                  </TableCell>
                  <TableCell>
                    <span className={`badge border ${getPriorityColor(ticket.priority)}`}>
                      {ticket.priority}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">{formatDateTime(ticket.created_at)}</TableCell>
                  <TableCell>
                    <StatusBadge status={ticket.status} />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openUpdateDialog(ticket)}
                      data-testid={`update-ticket-${ticket.id}`}
                    >
                      Update
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
          <p className="text-sm text-muted-foreground">
            Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={(page + 1) * limit >= total}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Update Dialog */}
      <Dialog open={updateDialog} onOpenChange={setUpdateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Ticket</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedTicket && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">{selectedTicket.subject}</p>
                <p className="text-sm text-muted-foreground mt-1">{selectedTicket.description}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Resolution Notes</Label>
              <Textarea
                placeholder="Enter resolution notes..."
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                rows={4}
                data-testid="resolution-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpdateDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdate} data-testid="confirm-update-ticket-btn">
              Update Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
